// Third Party
import * as React from 'react';
import Image from 'next/image';
import cx from 'classnames';
import { toInteger } from 'lodash';
import moment from 'moment';

// Utils
import {
  Account,
  Feature,
  GalleryAsset,
  Listing,
  Maybe,
  PropertyType,
  SearchPromotionType,
  PriceDisplay,
} from '@gql';
import { useWindowSize } from '@hooks';
import {
  GRAY_PIXEL_DATA_URL,
  enumToStringPropertyTypesAbr,
  enumToStringUniquePropertyTypes,
} from '@utils/constants';
import { formatWholeDollarAmount } from '@utils/money';
import {
  imagekitLoader,
  imagekitProgressiveLoader,
} from '@utils/imagekit-loader';
import { concatenateAndReplace } from '@utils/test-helpers';
import { formatDateMMMD } from '@utils/date-helpers';
import { abbreviateState } from '@utils/abbreviate-state';
import { useAnalytics } from '@contexts/use-analytics';
import { AnalyticsRef } from '@components/analytics-ref';

// UI
import { FavoriteButton } from '@components/favorite-button';
import { Icon, IconNames } from '@components/icon';
import { IconTooltip } from '@components/icon-tooltip';
import { Link } from '@components/link';
import { DiamondListingLevelBadge } from '@components/listing-card/diamond-listing-level-badge';
import { Typography } from '@components/typography';
import { Carousel } from '@components/carousel';

export type ListingPartial = Pick<
  Listing,
  | 'bookDirectWebsiteUrl'
  | 'city'
  | 'description'
  | 'marketingWebsiteUrl'
  | 'name'
  | 'slug'
  | 'stateOrProvinceLongName'
  | 'guestCapacity'
  | 'numberOfRooms'
  | 'numberOfBathrooms'
  | 'minPrice'
  | 'maxPrice'
  | 'propertyType'
  | 'uniquePropertyType'
  | 'id'
  | 'isFavorited'
  | 'listingLevel'
  | 'searchableAvailability'
  | 'isDiamondCollection'
  | 'searchPromotionType'
  | 'reviewsAverageRating'
  | 'reviewsCount'
> & {
  galleryAssets: Array<Pick<GalleryAsset, 'id' | 'url' | 'description'>>;
  priceDisplay?: Pick<
    PriceDisplay,
    | 'baseAmountCents'
    | 'checkInDate'
    | 'checkOutDate'
    | 'nights'
    | 'subtotalWithoutTaxesCents'
  >;
  features?: Maybe<
    Array<Pick<Feature, 'key' | 'displayName' | 'categoryName' | 'promoted'>>
  >;
  account: Pick<Account, 'companyName' | 'slug' | 'avatarUrl'>;
};

const rateBeforeSavings = (nightlyRate: number): number => {
  return nightlyRate * 1.15;
};

export interface IListingCardProps {
  listing: ListingPartial;
  isFavorited: boolean;
  altTag: string;
  showRoute: string;
  accountShowRoute?: string;
  forceMobileView?: boolean;
  displayStay?: boolean;
  avatarUrl?: string;
  priority?: boolean;
  onFavoritedSuccess?: (isFavorited: boolean) => void;
}

type SliderImage = {
  url: string;
};

const smallScreenSize = 640;

export const ListingCard: React.FC<IListingCardProps> = ({
  listing,
  altTag,
  isFavorited,
  showRoute,
  forceMobileView = false,
  displayStay = false,
  priority,
  onFavoritedSuccess,
}) => {
  const displayTotals = true;
  const featured = listing.searchPromotionType === SearchPromotionType.Featured;
  const { width: screenWidth } = useWindowSize();

  const shouldShowAllImages = listing.galleryAssets.length > 1;

  const shouldShowGuestCapacity =
    (!displayStay || !listing.priceDisplay) &&
    (listing.numberOfRooms || listing.guestCapacity) &&
    listing.propertyType !== PropertyType.BoutiqueHotel &&
    listing.propertyType !== PropertyType.BedAndBreakfast;

  let images: SliderImage[] = [];

  if (
    !Array.isArray(listing.galleryAssets) ||
    listing.galleryAssets.length > 0
  ) {
    listing.galleryAssets.forEach((galleryAsset) => {
      if (galleryAsset.url) {
        images.push({ url: galleryAsset.url });
      }
    });
  }

  // Limit the number of images per listing to 10
  images = images.slice(0, 10);

  const location = `${listing.city}, ${abbreviateState(listing.stateOrProvinceLongName)}`;
  const propertyType = listing.uniquePropertyType
    ? enumToStringUniquePropertyTypes(listing.uniquePropertyType)
    : listing.propertyType
      ? enumToStringPropertyTypesAbr(listing.propertyType)
      : null;

  const { setCurrentListing } = useAnalytics();

  const handleListingClick = () => {
    setCurrentListing(listing);
  };

  return (
    <AnalyticsRef name="listing">
      {/* tablet - desktop nav */}
      <div
        className={cx(
          'hidden sm:block w-full h-full',
          forceMobileView && 'sm:hidden'
        )}
      >
        <div className="h-full w-full p-2">
          <div className="w-full">
            <div className="relative h-full aspect-[100/95] rounded-xl overflow-hidden">
              <div className="w-full">
                <Carousel
                  showDots={shouldShowAllImages}
                  showArrows={shouldShowAllImages}
                  loadingStrategy={priority ? 'progressive' : 'ondemand'}
                >
                  {images.map((image, index) => (
                    <Link
                      to={showRoute}
                      onClick={handleListingClick}
                      key={index}
                      target={
                        screenWidth <= smallScreenSize ? undefined : '_blank'
                      }
                      className="block w-full h-full relative aspect-[100/95]"
                    >
                      <Image
                        layout="fill"
                        src={image.url}
                        alt={altTag}
                        placeholder="blur"
                        className="object-center object-cover"
                        loader={
                          index === 0 && priority
                            ? imagekitProgressiveLoader
                            : imagekitLoader
                        }
                        blurDataURL={GRAY_PIXEL_DATA_URL}
                        lazyBoundary="400px"
                        sizes="(min-width: 1536px) calc(16.67vw - 24px), (min-width: 1280px) calc(25vw - 24px), (min-width: 640px) calc(50vw - 32px), calc(100vw - 32px)"
                        {...(index === 0 && priority
                          ? { priority: true, fetchpriority: 'high' }
                          : { loading: 'lazy' })}
                        data-testid={concatenateAndReplace(
                          `${listing.name}-image-${index}`
                        )}
                      />
                    </Link>
                  ))}
                </Carousel>

                {featured && (
                  <>
                    <div className="absolute top-0 left-0 w-full bg-primary-main z-50 p-2 flex items-center justify-center">
                      <Typography
                        component="h4"
                        variant="subtitle1"
                        className="text-white font-bold w-full text-center"
                      >
                        Featured Property
                      </Typography>

                      <IconTooltip
                        icon={IconNames.INFO_CIRCLE}
                        placement="bottom"
                        className="text-white absolute right-4"
                      >
                        This is a sponsored listing displayed based on your
                        search criteria.
                      </IconTooltip>
                    </div>

                    <div className="absolute top-12 right-3.5 rounded-lg">
                      <FavoriteButton
                        listingID={listing.id}
                        listingIsFavorited={isFavorited}
                        onFavoritedSuccess={onFavoritedSuccess}
                      />
                    </div>
                  </>
                )}

                {!featured && (
                  <>
                    <div className="absolute top-4 right-3.5 rounded-lg">
                      <FavoriteButton
                        listingID={listing.id}
                        listingIsFavorited={isFavorited}
                        onFavoritedSuccess={onFavoritedSuccess}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <Link
              to={showRoute}
              onClick={handleListingClick}
              target={screenWidth <= smallScreenSize ? undefined : '_blank'}
              className="no-underline flex flex-row w-full items-top justify-between mt-2 px-1"
            >
              <div className="flex w-full text-primary-main text-sm font-semibold max-w-sm">
                <div className="flex flex-col w-full justify-between space-y-1">
                  <div className="space-y-1 w-full text-primary-main">
                    <div className="flex flex-row w-full justify-between items-center space-x-1 max-w-sm">
                      <div
                        className="flex-1 text-sm font-semibold line-clamp-1"
                        data-testid={concatenateAndReplace(`${listing.name}`)}
                      >
                        {listing.name}
                      </div>
                      {/* Ratings */}
                      {listing.reviewsAverageRating ? (
                        <div className="flex-shrink-0 text-xs font-medium whitespace-nowrap">
                          <div className="flex flex-row space-x-0.5 items-center">
                            <Icon
                              name={IconNames.STAR}
                              solid={true}
                              size="xs"
                              className="pr-[1px]"
                            />
                            <div>
                              {Number.isInteger(listing.reviewsAverageRating)
                                ? listing.reviewsAverageRating?.toFixed(1)
                                : listing.reviewsAverageRating?.toFixed(2)}
                            </div>
                            {listing.reviewsCount && (
                              <div>{'( ' + listing.reviewsCount + ' )'}</div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-row w-full justify-between space-x-1 max-w-sm">
                      <div
                        className="flex-shrink text-gray-main text-sm font-light line-clamp-1"
                        data-testid={concatenateAndReplace(
                          `${propertyType}-in-${location}`
                        )}
                      >
                        {propertyType
                          ? `${propertyType} in ${location}`
                          : location}
                      </div>
                    </div>
                  </div>

                  {shouldShowGuestCapacity ? (
                    <div className="flex-shrink text-gray-main text-sm font-light line-clamp-1">
                      <div className="flex space-x-1.5">
                        {listing.numberOfRooms ? (
                          <div className="flex items-center">
                            {listing.numberOfRooms}{' '}
                            {listing.numberOfRooms > 1
                              ? ' bedrooms'
                              : ' bedroom'}
                          </div>
                        ) : null}
                        {listing.numberOfRooms && listing.guestCapacity ? (
                          <div className="flex items-center">&bull;</div>
                        ) : null}
                        {listing.guestCapacity ? (
                          <>
                            <div className="flex items-center">
                              {listing.guestCapacity}{' '}
                              {listing.guestCapacity > 1 ? ' guests' : ' guest'}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {listing.account.companyName ? (
                    <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">
                      Hosted by {listing.account.companyName}
                    </div>
                  ) : null}

                  {displayStay && listing.priceDisplay ? (
                    <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">
                      {formatDateMMMD(moment(listing.priceDisplay.checkInDate))}
                      {' – '}
                      {formatDateMMMD(
                        moment(listing.priceDisplay.checkOutDate)
                      )}
                    </div>
                  ) : null}

                  {listing.maxPrice ||
                  listing.minPrice ||
                  (listing.priceDisplay &&
                    listing.priceDisplay.baseAmountCents > 100) ? (
                    <div className="flex space-x-1 text-primary-main text-sm font-semibold text-ellipsis overflow-hidden pt-0.5">
                      {listing.priceDisplay &&
                      listing.priceDisplay.baseAmountCents > 100 ? (
                        <>
                          <div className="line-through text-gray-main">
                            {formatWholeDollarAmount(
                              toInteger(
                                rateBeforeSavings(
                                  listing.priceDisplay.baseAmountCents /
                                    listing.priceDisplay.nights
                                )
                              )
                            )}
                          </div>
                          <div>
                            {formatWholeDollarAmount(
                              toInteger(
                                listing.priceDisplay.baseAmountCents /
                                  listing.priceDisplay.nights
                              )
                            )}
                          </div>
                          <div className="font-medium"> night</div>
                          {displayTotals &&
                            listing.priceDisplay.subtotalWithoutTaxesCents && (
                              <>
                                <div className="font-medium">&nbsp;·&nbsp;</div>
                                <div>
                                  {formatWholeDollarAmount(
                                    toInteger(
                                      listing.priceDisplay
                                        .subtotalWithoutTaxesCents
                                    )
                                  )}
                                </div>
                                <div className="font-medium"> total</div>
                              </>
                            )}
                        </>
                      ) : listing.minPrice || listing.maxPrice ? (
                        listing.minPrice && listing.maxPrice ? (
                          <>
                            <div>Averages</div>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(
                                  rateBeforeSavings(
                                    (listing.minPrice + listing.maxPrice) / 2
                                  )
                                )
                              )}
                            </div>
                            <div>
                              {formatWholeDollarAmount(
                                toInteger(
                                  (listing.minPrice + listing.maxPrice) / 2
                                )
                              )}
                            </div>
                            <div className="font-medium"> night</div>
                          </>
                        ) : listing.minPrice ? (
                          <>
                            <div>Starts at</div>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(rateBeforeSavings(listing.minPrice))
                              )}
                            </div>
                            <div>
                              {formatWholeDollarAmount(listing.minPrice)}
                            </div>
                            <div className="font-medium"> night</div>
                          </>
                        ) : listing.maxPrice ? (
                          <>
                            <div>Peaks at</div>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(rateBeforeSavings(listing.maxPrice))
                              )}
                            </div>
                            <div>
                              {formatWholeDollarAmount(listing.maxPrice)}
                            </div>
                            <div className="font-medium"> night</div>
                          </>
                        ) : null
                      ) : null}
                      {listing.maxPrice && !listing.minPrice ? (
                        <>
                          <div>{`Peaks at ${formatWholeDollarAmount(
                            listing.maxPrice
                          )}`}</div>
                          <div className="font-medium"> night</div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* mobile nav */}
      <div className={cx('block sm:block', !forceMobileView && 'sm:hidden')}>
        <div className="flex h-full pt-6 px-1">
          <div className="relative w-full aspect-[100/95] cursor-pointer overflow-hidden rounded-xl">
            <div>
              <Carousel
                showDots={shouldShowAllImages}
                showArrows={shouldShowAllImages}
                loadingStrategy={priority ? 'progressive' : 'ondemand'}
              >
                {images.map((image, index) => (
                  <Link
                    to={showRoute}
                    onClick={handleListingClick}
                    key={index}
                    target={
                      screenWidth <= smallScreenSize ? undefined : '_blank'
                    }
                    className="block w-full h-full relative aspect-[100/95]"
                  >
                    <Image
                      layout="fill"
                      src={image.url}
                      alt={altTag}
                      placeholder="blur"
                      className="object-center object-cover"
                      loader={imagekitLoader}
                      blurDataURL={GRAY_PIXEL_DATA_URL}
                      lazyBoundary="400px"
                      sizes="(min-width: 1536px) calc(16.67vw - 24px), (min-width: 1280px) calc(25vw - 24px), (min-width: 640px) calc(50vw - 32px), calc(100vw - 32px)"
                      loading="lazy"
                      data-testid={concatenateAndReplace(
                        `${listing.name}-image-${index}`
                      )}
                    />
                  </Link>
                ))}
              </Carousel>
              {listing.isDiamondCollection && (
                <div className="absolute top-4 left-4">
                  <DiamondListingLevelBadge />
                </div>
              )}

              {featured && (
                <div className="absolute top-0 left-0 w-full bg-primary-main z-45 p-2 flex items-center justify-center">
                  <Typography
                    component="h4"
                    variant="subtitle1"
                    className="text-white font-bold w-full text-center"
                  >
                    Featured Property
                  </Typography>
                </div>
              )}

              <div
                className={cx('absolute right-3.5 rounded-lg', {
                  'top-4': !featured,
                  'top-12': featured,
                })}
              >
                <FavoriteButton
                  listingID={listing.id}
                  listingIsFavorited={isFavorited}
                  onFavoritedSuccess={onFavoritedSuccess}
                  size="md"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-row items-top justify-between mt-2 px-1">
          <div className="w-full">
            <div className="flex flex-col w-full justify-between mt-1.5">
              <a href={showRoute} onClick={handleListingClick}>
                <div className="space-y-0.5 w-full text-primary-main">
                  <div className="flex flex-row w-full items-center justify-between space-x-1">
                    <div
                      className="flex-1 text-sm font-semibold line-clamp-1"
                      data-testid={concatenateAndReplace(`${listing.name}`)}
                    >
                      {listing.name}
                    </div>

                    {/* Ratings */}
                    {listing.reviewsAverageRating ? (
                      <div className="flex-shrink-0 text-xs font-medium whitespace-nowrap">
                        <div className="flex flex-row items-center space-x-0.5">
                          <Icon name={IconNames.STAR} solid={true} size="xs" />
                          <div>
                            {Number.isInteger(listing.reviewsAverageRating)
                              ? listing.reviewsAverageRating?.toFixed(1)
                              : listing.reviewsAverageRating?.toFixed(2)}
                          </div>
                          {listing.reviewsCount && (
                            <div>{'( ' + listing.reviewsCount + ' )'}</div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-row w-full justify-between space-x-1">
                    <div className="flex-shrink text-gray-main text-sm font-light line-clamp-1">
                      {propertyType
                        ? `${propertyType} in ${location}`
                        : location}
                    </div>
                  </div>

                  {shouldShowGuestCapacity ? (
                    <div className="flex-shrink text-gray-main text-sm font-light line-clamp-1">
                      <div className="flex space-x-1.5">
                        {listing.numberOfRooms ? (
                          <div className="flex items-center">
                            {listing.numberOfRooms}{' '}
                            {listing.numberOfRooms > 1
                              ? ' bedrooms'
                              : ' bedroom'}
                          </div>
                        ) : null}
                        {listing.numberOfRooms && listing.guestCapacity ? (
                          <div className="flex items-center">&bull;</div>
                        ) : null}
                        {listing.guestCapacity ? (
                          <>
                            <div className="flex items-center">
                              {listing.guestCapacity}{' '}
                              {listing.guestCapacity > 1 ? ' guests' : ' guest'}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {listing.account.companyName ? (
                    <div className="flex text-gray-main text-sm font-light text-ellipsis overflow-hidden">
                      Hosted by {listing.account.companyName}
                    </div>
                  ) : null}

                  {displayStay && listing.priceDisplay ? (
                    <div className="flex-shrink line-clamp-1 text-gray-main text-sm font-light text-ellipsis overflow-hidden">
                      {formatDateMMMD(moment(listing.priceDisplay.checkInDate))}
                      {' – '}
                      {formatDateMMMD(
                        moment(listing.priceDisplay.checkOutDate)
                      )}
                    </div>
                  ) : null}

                  {listing.maxPrice ||
                  listing.minPrice ||
                  (listing.priceDisplay &&
                    listing.priceDisplay.baseAmountCents > 100) ? (
                    <div className="text-primary-main text-sm font-semibold mt-0.5">
                      <div className="flex space-x-1 text-primary-main text-sm font-semibold text-ellipsis overflow-hidden pt-0.5">
                        {listing.priceDisplay &&
                        listing.priceDisplay.baseAmountCents > 100 ? (
                          <>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(
                                  rateBeforeSavings(
                                    listing.priceDisplay.baseAmountCents /
                                      listing.priceDisplay.nights
                                  )
                                )
                              )}
                            </div>
                            <div>
                              {formatWholeDollarAmount(
                                toInteger(
                                  listing.priceDisplay.baseAmountCents /
                                    listing.priceDisplay.nights
                                )
                              )}
                            </div>
                            <div className="font-medium"> night</div>
                            {displayTotals &&
                              listing.priceDisplay
                                .subtotalWithoutTaxesCents && (
                                <>
                                  <div className="font-medium">
                                    &nbsp;·&nbsp;
                                  </div>
                                  <div>
                                    {formatWholeDollarAmount(
                                      toInteger(
                                        listing.priceDisplay
                                          .subtotalWithoutTaxesCents
                                      )
                                    )}
                                  </div>
                                  <div className="font-medium"> total</div>
                                </>
                              )}
                          </>
                        ) : listing.minPrice || listing.maxPrice ? (
                          listing.minPrice && listing.maxPrice ? (
                            <>
                              <div>Averages</div>
                              <div className="line-through text-gray-main">
                                {formatWholeDollarAmount(
                                  toInteger(
                                    rateBeforeSavings(
                                      (listing.minPrice + listing.maxPrice) / 2
                                    )
                                  )
                                )}
                              </div>
                              <div>
                                {formatWholeDollarAmount(
                                  toInteger(
                                    (listing.minPrice + listing.maxPrice) / 2
                                  )
                                )}
                              </div>
                              <div className="font-medium"> night</div>
                            </>
                          ) : listing.minPrice ? (
                            <>
                              <div>Starts at</div>
                              <div className="line-through text-gray-main">
                                {formatWholeDollarAmount(
                                  toInteger(rateBeforeSavings(listing.minPrice))
                                )}
                              </div>
                              <div>
                                {formatWholeDollarAmount(listing.minPrice)}
                              </div>
                              <div className="font-medium"> night</div>
                            </>
                          ) : listing.maxPrice ? (
                            <>
                              <div>Peaks at</div>
                              <div className="line-through text-gray-main">
                                {formatWholeDollarAmount(
                                  toInteger(rateBeforeSavings(listing.maxPrice))
                                )}
                              </div>
                              <div>
                                {formatWholeDollarAmount(listing.maxPrice)}
                              </div>
                              <div className="font-medium"> night</div>
                            </>
                          ) : null
                        ) : null}
                        {listing.maxPrice && !listing.minPrice ? (
                          <>
                            <div>{`Peaks at ${formatWholeDollarAmount(
                              listing.maxPrice
                            )}`}</div>
                            <div className="font-medium"> night</div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </AnalyticsRef>
  );
};
